import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Case, CaseFilters, PaginatedResponse } from '../types';
import { LoadingSpinner } from './LoadingSpinner';
import { apiClient } from '../services/apiClient';
import { getRoleDisplay, getRoleBadgeClass } from '../utils/roles';
import { getMedicalRoleLabel } from '../utils/medicalRoles';
import { MEDICAL_TERMINOLOGY } from '../constants/medicalTerminology';

interface CaseBrowserProps {
  initialFilters?: Partial<CaseFilters>;
  showMyCases?: boolean;
}

const MEDICAL_SYSTEMS = MEDICAL_TERMINOLOGY.MEDICAL_SYSTEMS;

const TISSUE_TYPES = [
  'Ήπαρ',
  'Νεφρός',
  'Πνεύμονας',
  'Μαστός',
  'Προστάτης',
  'Δέρμα',
  'Γαστρεντερικό',
  'Γυναικολογικό',
  'Λεμφαδένας',
  'Οστό',
  'Μαλακά Μόρια',
  'Άλλο'
];

const URGENCY_LEVELS = [
  { value: 'low', label: 'Εκπαιδευτικό', color: 'bg-green-100 text-green-800' },
  { value: 'normal', label: 'Κανονική', color: 'bg-blue-100 text-blue-800' },
  { value: 'high', label: 'Υψηλή', color: 'bg-orange-100 text-orange-800' },
  { value: 'urgent', label: 'Επείγον', color: 'bg-red-100 text-red-800' }
];


export function CaseBrowser({ initialFilters = {}, showMyCases = false }: CaseBrowserProps) {
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasMore: false
  });

  const [filters, setFilters] = useState<CaseFilters>({
    page: 1,
    limit: 20,
    status: 'open',
    ...initialFilters,
    // Ensure urgent cases appear first by default (unless explicitly overridden)
    sortBy: initialFilters.sortBy || 'urgency_priority',
    sortOrder: initialFilters.sortOrder || 'desc'
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchCases = useCallback(async (isLoadMore = false) => {
    try {
      if (!isLoadMore) {
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);

      // Build query parameters
      const queryParams: Record<string, string> = {};
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          queryParams[key] = String(value);
        }
      });

      if (searchQuery.trim()) {
        queryParams.search = searchQuery.trim();
      }

      if (showMyCases) {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user.id) {
          queryParams.userId = user.id;
        }
      }

      const searchParams = new URLSearchParams(queryParams);
      const response = await apiClient.get(`/cases?${searchParams}`);

      const data = response.data;
      
      if (isLoadMore) {
        setCases(prev => [...prev, ...data.data.cases]);
      } else {
        setCases(data.data.cases);
      }
      
      // Use proper pagination from API
      setPagination({
        currentPage: data.data.page,
        totalPages: data.data.totalPages,
        totalCount: data.data.totalCount,
        hasMore: data.data.hasNext
      });

    } catch (err: any) {
      console.error('Error fetching cases:', err);
      setError(err.response?.data?.message || err.message || 'Άγνωστο σφάλμα');
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, [filters, searchQuery, showMyCases]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Debounce search
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setFilters(prev => ({ ...prev, page: 1 }));
    }, 500);

    setSearchTimeout(timeout);
  };

  const handleFilterChange = (key: keyof CaseFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page when filtering
    }));
  };

  const handleLoadMore = () => {
    if (pagination.hasMore && !isLoadingMore) {
      setFilters(prev => ({
        ...prev,
        page: prev.page! + 1
      }));
      fetchCases(true);
    }
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 20,
      sortBy: 'urgency_priority',
      sortOrder: 'desc',
      status: 'open'
    });
    setSearchQuery('');
  };

  const getUrgencyColor = (urgency: string) => {
    const level = URGENCY_LEVELS.find(l => l.value === urgency);
    return level?.color || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-green-100 text-green-800';
      case 'resolved':
        return 'bg-blue-100 text-blue-800';
      case 'archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'open':
        return 'Ανοικτό';
      case 'resolved':
        return 'Λυμένο';
      case 'archived':
        return 'Αρχειοθετημένο';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      return 'Πριν από λίγο';
    } else if (diffInHours < 24) {
      return `Πριν από ${diffInHours} ώρες`;
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays === 1) {
        return 'Χθες';
      } else if (diffInDays < 7) {
        return `Πριν από ${diffInDays} μέρες`;
      } else {
        return date.toLocaleDateString('el-GR');
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-20">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {showMyCases ? 'Τα Περιστατικά μου' : 'Περιστατικά'}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {pagination.totalCount} συνολικά περιστατικά
          </p>
        </div>
        
        <div className="mt-4 sm:mt-0 flex space-x-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            🔍 Φίλτρα
          </button>
          
          {!showMyCases && (
            <Link
              to="/cases/new"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              + Νέο Περιστατικό
            </Link>
          )}
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Αναζήτηση περιστατικών..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="text-gray-400">🔍</span>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Σύστημα
              </label>
              <select
                value={filters.caseType || ''}
                onChange={(e) => handleFilterChange('caseType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Όλα</option>
                {MEDICAL_SYSTEMS.map(system => (
                  <option key={system} value={system}>{system}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Προτεραιότητα
              </label>
              <select
                value={filters.urgencyLevel || ''}
                onChange={(e) => handleFilterChange('urgencyLevel', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Όλα</option>
                {URGENCY_LEVELS.map(level => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ταξινόμηση
              </label>
              <select
                value={`${filters.sortBy}_${filters.sortOrder}`}
                onChange={(e) => {
                  const parts = e.target.value.split('_');
                  if (parts.length === 3 && parts[0] === 'urgency' && parts[1] === 'priority') {
                    // Handle urgency_priority_desc/asc
                    handleFilterChange('sortBy', 'urgency_priority');
                    handleFilterChange('sortOrder', parts[2]);
                  } else {
                    // Handle other cases
                    const sortBy = parts.slice(0, -1).join('_');
                    const sortOrder = parts[parts.length - 1];
                    handleFilterChange('sortBy', sortBy);
                    handleFilterChange('sortOrder', sortOrder);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              >
                <option value="urgency_priority_desc">Επείγοντα πρώτα</option>
                <option value="created_at_desc">Νεότερα πρώτα</option>
                <option value="created_at_asc">Παλαιότερα πρώτα</option>
                <option value="title_asc">Τίτλος (Α-Ω)</option>
                <option value="title_desc">Τίτλος (Ω-Α)</option>
                <option value="urgency_level_desc">Προτεραιότητα μόνο</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Καθαρισμός Φίλτρων
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => fetchCases()}
            className="mt-2 text-red-600 hover:text-red-800 underline"
          >
            Προσπάθεια ξανά
          </button>
        </div>
      )}

      {/* Cases Grid */}
      {!loading && !error && (
        <>
          {cases.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📋</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Δεν βρέθηκαν περιστατικά
              </h3>
              <p className="text-gray-600 mb-6">
                {showMyCases 
                  ? 'Δεν έχετε δημιουργήσει περιστατικά ακόμα.'
                  : 'Δοκιμάστε να αλλάξετε τα φίλτρα αναζήτησης.'
                }
              </p>
              {showMyCases && (
                <Link
                  to="/cases/new"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Δημιουργία Πρώτου Περιστατικού
                </Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cases.map((caseItem) => (
                <Link
                  key={caseItem.id}
                  to={`/cases/${caseItem.id}`}
                  className={`rounded-lg shadow-sm border hover:shadow-md transition-all duration-200 ${
                    caseItem.urgencyLevel === 'urgent' 
                      ? 'bg-red-50 border-red-300 ring-2 ring-red-100 hover:ring-red-200' 
                      : 'bg-white border-gray-200'
                  }`}
                >
                  <div className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {caseItem.urgencyLevel === 'urgent' && (
                            <span className="text-red-600 text-lg animate-pulse" title="Επείγον">
                              🚨
                            </span>
                          )}
                          <h3 className={`text-lg font-semibold truncate ${
                            caseItem.urgencyLevel === 'urgent' ? 'text-red-900' : 'text-gray-900'
                          }`}>
                            {caseItem.title}
                          </h3>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {caseItem.caseType}
                          {caseItem.tissueType && ` • ${caseItem.tissueType}`}
                        </p>
                      </div>
                      <div className="flex flex-col items-end space-y-1 ml-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUrgencyColor(caseItem.urgencyLevel)}`}>
                          {URGENCY_LEVELS.find(l => l.value === caseItem.urgencyLevel)?.label}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(caseItem.status)}`}>
                          {getStatusLabel(caseItem.status)}
                        </span>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-gray-700 text-sm line-clamp-3 mb-4">
                      {caseItem.description}
                    </p>

                    {/* Images Preview */}
                    {caseItem.images && caseItem.images.length > 0 && (
                      <div className="mb-4">
                        <div className="flex space-x-2 overflow-x-auto">
                          {caseItem.images.slice(0, 3).map((image, index) => (
                            <img
                              key={index}
                              src={image.imageUrl}
                              alt={`Case image ${index + 1}`}
                              className="w-16 h-16 object-cover rounded-md flex-shrink-0"
                            />
                          ))}
                          {caseItem.images.length > 3 && (
                            <div className="w-16 h-16 bg-gray-100 rounded-md flex-shrink-0 flex items-center justify-center">
                              <span className="text-xs text-gray-600">
                                +{caseItem.images.length - 3}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Patient Info (if available) */}
                    {(caseItem.patientAge || caseItem.patientGender) && (
                      <div className="mb-4 text-sm text-gray-600">
                        <span className="inline-flex items-center">
                          👤{' '}
                          {caseItem.patientAge && `${caseItem.patientAge} ετών`}
                          {caseItem.patientAge && caseItem.patientGender && ', '}
                          {caseItem.patientGender === 'male' && 'Άνδρας'}
                          {caseItem.patientGender === 'female' && 'Γυναίκα'}
                          {caseItem.patientGender === 'other' && 'Άλλο'}
                        </span>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center">
                          👁️ {caseItem.viewCount || 0}
                        </span>
                        <span className="flex items-center">
                          💬 {caseItem.commentCount || 0}
                        </span>
                      </div>
                      <div className="text-right">
                        {caseItem.author && (
                          <div className="space-y-1">
                            <p className="font-medium text-gray-900">
                              Δρ. {caseItem.author.firstName} {caseItem.author.lastName}
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {caseItem.author.medicalRole && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  {getMedicalRoleLabel(caseItem.author.medicalRole, caseItem.author.sex)}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        <p className="mt-1">{formatDate(caseItem.createdAt.toString())}</p>
                        {caseItem.status === 'resolved' && caseItem.resolvedAt && (
                          <p className="text-xs text-blue-600 mt-1">
                            ✅ Επιλύθηκε {formatDate(caseItem.resolvedAt.toString())}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {/* Load More Button */}
          {pagination.hasMore && !isLoadingMore && (
            <div className="text-center mt-8">
              <button
                onClick={handleLoadMore}
                className="inline-flex items-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Φόρτωση περισσότερων
              </button>
            </div>
          )}

          {/* Loading More Indicator */}
          {isLoadingMore && (
            <div className="text-center mt-8">
              <LoadingSpinner />
            </div>
          )}
        </>
      )}
    </div>
  );
}